import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Search, Headphones, ListOrdered, ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime } from "@/lib/date";
import { AdminSync } from "@/pages/Vetting";

const ICON = { "t1g-sepl": Headphones, "sepl-master": ListOrdered };
const match = (q, ...xs) => !q || xs.some((x) => (x || "").toLowerCase().includes(q));

function TalkList({ stage, q }) {
  const groups = stage.groups.map((g) => ({ ...g, talks: g.talks.filter((t) => match(q, t.title, t.speaker, t.platform, t.notes)) }))
    .filter((g) => g.talks.length);
  if (!groups.length) return <p className="text-sm text-slate-500" data-testid="talks-empty">No talks match your search.</p>;
  return groups.map((g, gi) => (
    <section key={g.label} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid={`talks-group-${gi}`}>
      <h2 className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#8A6D1F] mb-3">{g.label}</h2>
      <ul className="divide-y divide-slate-100">
        {g.talks.map((t, i) => (
          <li key={i} className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold text-[#0A1F44] break-words">{t.title}</div>
              <div className="text-sm text-slate-600">{t.speaker}{t.notes && <span className="text-slate-500"> · {t.notes}</span>}</div>
            </div>
            {t.platform && <span className="self-start sm:self-center text-[11px] font-semibold tracking-wide text-[#0A1F44] bg-[#C9A961]/15 rounded px-2 py-0.5 shrink-0">{t.platform}</span>}
          </li>
        ))}
      </ul>
    </section>
  ));
}

function MasterList({ stage, q }) {
  const talks = stage.talks.filter((t) => match(q, t.number, t.topic, t.speaker));
  if (!talks.length) return <p className="text-sm text-slate-500" data-testid="talks-empty">No talks match your search.</p>;
  return (
    <ul className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="master-list">
      {talks.map((t, i) => (
        <li key={i} className="px-4 py-2.5 flex items-start gap-3">
          <span className="w-24 shrink-0 text-xs font-semibold text-[#8A6D1F] pt-0.5">{t.number}</span>
          <div className="min-w-0">
            <div className="text-[15px] font-medium text-[#0A1F44] break-words">{t.topic}</div>
            {t.speaker && <div className="text-sm text-slate-600">{t.speaker}</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function TalkLibrary() {
  const { listId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const load = async () => {
    try { setData((await api.get("/training/talks")).data); setError(null); }
    catch (e) { setError(e?.response?.data?.detail || "Couldn't load the Podcast Library. Try again shortly."); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setQ(""); window.scrollTo(0, 0); }, [listId]);
  const stage = useMemo(() => data?.stages.find((s) => s.id === listId), [data, listId]);
  if (!listId) return <Navigate to="/talk-library/t1g-sepl" replace />;
  if (error) return <div className="text-sm rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="talks-error">{error}</div>;
  if (!data) return <div className="text-sm text-slate-500" data-testid="talks-loading">Loading Podcast Library…</div>;
  if (!stage) return <Navigate to="/talk-library/t1g-sepl" replace />;
  return (
    <div className="space-y-5 max-w-4xl" data-testid="talk-library-page">
      <Link to="/hub" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#0A1F44] min-h-[44px]" data-testid="talks-back"><ChevronLeft className="w-4 h-4" /> Resources</Link>
      <div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Podcast Library</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#0A1F44] mt-1" data-testid="talks-title">{stage.nav}</h1>
        <p className="text-sm text-slate-600 mt-1" data-testid="talks-intro">{stage.intro}</p>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-slate-500">
          <span data-testid="talks-synced">Updated {fmtDateTime(data.synced_at)}</span>
          {user?.can_sync_content && <AdminSync slug="talks" onDone={load} />}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#0A1F44]/5 p-1" role="tablist" data-testid="talks-tabs">
        {data.stages.map((s) => {
          const Icon = ICON[s.id] || Headphones;
          const on = s.id === stage.id;
          return (
            <Link key={s.id} to={`/talk-library/${s.id}`} role="tab" aria-selected={on} data-testid={`talks-tab-${s.id}`}
                  className={`min-h-[44px] flex items-center justify-center gap-2 rounded-lg px-2 text-sm font-medium text-center transition-colors ${on ? "bg-[#0A1F44] text-white" : "text-[#0A1F44] hover:bg-white"}`}>
              <Icon className="w-4 h-4 shrink-0" /> <span>{s.nav}</span> <span className={`text-xs ${on ? "text-[#C9A961]" : "text-slate-500"}`}>{s.count}</span>
            </Link>
          );
        })}
      </div>
      <label className="relative block">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={stage.type === "master" ? "Search number, topic or speaker" : "Search title, speaker or platform"}
               className="w-full h-11 rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]" data-testid="talks-search" aria-label="Search talks" />
      </label>
      <div className="space-y-4" data-testid={`talks-list-${stage.id}`}>
        {stage.type === "master" ? <MasterList stage={stage} q={q.trim().toLowerCase()} /> : <TalkList stage={stage} q={q.trim().toLowerCase()} />}
      </div>
    </div>
  );
}
